import api from './api';
import { API_ENDPOINTS, USE_MOCK_DATA } from '../constants/config';
import { mockCustomers, generateMockCustomers, getMockCompanies } from '../data/mockData';
import { 
  mockSites, 
  mockContracts,
  getSitesByCustomerId, 
  getTicketsBySiteId,
  getSiteStats,
  getTicketsHistoryBySite,
  getTicketsByPriorityForSite,
  getAMCStatusForSite,
  getTicketsByCustomerId,
  getCustomerTicketStats
} from '../data/mockData';
import { ROLES } from '../constants/roles';

class CustomerService {
  async getAllCustomers() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, data: mockCustomers });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.USERS_BY_ROLE(ROLES.CUSTOMER));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching customers:', error);
      return { success: false, error: error.message };
    }
  }

  async getCustomerById(customerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const customer = mockCustomers.find(c => c.id === customerId);
          if (customer) {
            resolve({ success: true, data: customer });
          } else {
            resolve({ success: false, error: 'Customer not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.USER_DETAILS(customerId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching customer:', error);
      return { success: false, error: error.message };
    }
  }

  async createCustomer(customerData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const newCustomer = {
            id: mockCustomers.length + 1,
            ...customerData,
            active_contracts: 0,
            total_sites: 0,
            open_tickets: 0,
            total_tickets: 0,
            contract_value: 0,
            joined_date: new Date().toISOString(),
            last_ticket: null
          };
          
          mockCustomers.unshift(newCustomer);
          resolve({ success: true, data: newCustomer });
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.USERS, { ...customerData, role: ROLES.CUSTOMER });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating customer:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async updateCustomer(customerId, customerData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const customerIndex = mockCustomers.findIndex(c => c.id === customerId);
          if (customerIndex !== -1) {
            mockCustomers[customerIndex] = {
              ...mockCustomers[customerIndex],
              ...customerData
            };
            resolve({ success: true, data: mockCustomers[customerIndex] });
          } else {
            resolve({ success: false, error: 'Customer not found' });
          }
        }, 500);
      });
    }

    try {
      const response = await api.put(API_ENDPOINTS.USER_DETAILS(customerId), customerData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating customer:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async deleteCustomer(customerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const customerIndex = mockCustomers.findIndex(c => c.id === customerId);
          if (customerIndex !== -1) {
            const customer = mockCustomers[customerIndex];
            if (customer.active_contracts > 0) {
              resolve({ 
                success: false, 
                error: `Cannot delete customer with ${customer.active_contracts} active contract(s)` 
              });
            } else if (customer.open_tickets > 0) {
              resolve({ 
                success: false, 
                error: `Cannot delete customer with ${customer.open_tickets} open ticket(s)` 
              });
            } else {
              mockCustomers.splice(customerIndex, 1);
              resolve({ success: true });
            }
          } else {
            resolve({ success: false, error: 'Customer not found' });
          }
        }, 500);
      });
    }

    try {
      await api.delete(API_ENDPOINTS.USER_DETAILS(customerId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting customer:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async getCustomerCompanies() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const companies = getMockCompanies();
          resolve({ success: true, data: companies });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.COMPANIES);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching customer companies:', error);
      return { success: false, error: error.message };
    }
  }

  async getActiveContract(customerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ 
            success: true, 
            data: {
              id: 'AMC-2026-0001',
              customer_id: customerId,
              status: 'Active',
              start_date: new Date().toISOString(),
              end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
            }
          });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.CUSTOMER_ACTIVE_CONTRACT(customerId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching active contract:', error);
      return { success: false, error: error.message };
    }
  }

  async getCustomerSites(customerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const sites = getSitesByCustomerId(customerId);
          resolve({ success: true, data: sites });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.CUSTOMER_SITES(customerId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching customer sites:', error);
      return { success: false, error: error.message };
    }
  }

  async getEscalationMatrix(customerId, siteId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            success: true,
            data: [
              {
                level: 1,
                name: 'Help Desk Coordinator',
                role: 'Service Desk',
                phone: '+91 98765 00001',
                email: 'helpdesk@amc.com',
                availability: '24 x 7',
                response_time: '15 minutes'
              },
              {
                level: 2,
                name: 'Site Team Lead',
                role: 'Team Lead',
                phone: '+91 98765 00002',
                email: 'sitelead@amc.com',
                availability: 'Mon-Sat, 8:00 AM - 8:00 PM',
                response_time: '30 minutes'
              },
              {
                level: 3,
                name: 'Regional Service Manager',
                role: 'Service Manager',
                phone: '+91 98765 00003',
                email: 'rsm@amc.com',
                availability: 'Mon-Sat, 9:00 AM - 7:00 PM',
                response_time: '1 hour'
              },
              {
                level: 4,
                name: 'Operations Head',
                role: 'Operations',
                phone: '+91 98765 00004',
                email: 'opshead@amc.com',
                availability: 'Business Hours',
                response_time: '2 hours'
              }
            ]
          });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.CUSTOMER_ESCALATION_MATRIX(customerId, siteId));
      return { success: true, data: Array.isArray(response.data) ? response.data : [] };
    } catch (error) {
      console.error('Error fetching escalation matrix:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async downloadEscalationMatrix(customerId, siteId, siteName = 'Site') {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const blob = new Blob(['Mock PDF Content'], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `escalation_matrix_${siteName.replace(' ', '_')}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          resolve({ success: true });
        }, 500);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.CUSTOMER_ESCALATION_MATRIX_DOWNLOAD(customerId, siteId), {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const filename = `escalation_matrix_${siteName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error downloading escalation matrix:', error);
      return { success: false, error: error.message };
    }
  }

  async getSiteDetails(siteId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const site = mockSites.find(s => s.id === siteId);
          if (site) {
            resolve({ success: true, data: site });
          } else {
            resolve({ success: false, error: 'Site not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.SITE_DETAILS(siteId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching site details:', error);
      return { success: false, error: error.message };
    }
  }

  async getDashboardData(customerId, siteId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const site = mockSites.find(s => s.id === siteId);
          if (!site) {
            resolve({ success: false, error: 'Site not found' });
            return;
          }

          const stats = getSiteStats(siteId);
          const history = getTicketsHistoryBySite(siteId);
          const byPriority = getTicketsByPriorityForSite(siteId);
          const amcStatus = getAMCStatusForSite(siteId);
          const siteContracts = mockContracts
            .filter((contract) => Number(contract.site_id) === Number(siteId))
            .sort((a, b) => new Date(b.end_date) - new Date(a.end_date));
          const activeContract = siteContracts.find((contract) => contract.status === 'Active');
          const contractForDisplay = activeContract || siteContracts[0] || null;
          const amcType = contractForDisplay?.contract_type || 'Non-Comprehensive';
          const recentTickets = getTicketsBySiteId(siteId)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5);

          resolve({
            success: true,
            data: {
              site,
              stats: {
                total: stats.total,
                open: stats.open,
                resolved: stats.resolved,
                amcStatus: amcStatus,
                amcType
              },
              charts: {
                history,
                byPriority
              },
              recentTickets
            }
          });
        }, 500);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.CUSTOMER_DASHBOARD(customerId, siteId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return { success: false, error: error.message };
    }
  }

  async getTicketsBySite(siteId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const tickets = getTicketsBySiteId(siteId);
          resolve({ success: true, data: tickets });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.TICKETS}?site_id=${siteId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching site tickets:', error);
      return { success: false, error: error.message };
    }
  }

  async getSiteStatistics(siteId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const stats = getSiteStats(siteId);
          resolve({ success: true, data: stats });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.SITE_STATS(siteId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching site statistics:', error);
      return { success: false, error: error.message };
    }
  }

  async getCustomerTickets(customerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const tickets = getTicketsByCustomerId(customerId);
          resolve({ success: true, data: tickets });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.TICKETS}?customer_id=${customerId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching customer tickets:', error);
      return { success: false, error: error.message };
    }
  }

  async getCustomerTicketStats(customerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const stats = getCustomerTicketStats(customerId);
          resolve({ success: true, data: stats });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.CUSTOMER_TICKET_STATS(customerId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching customer ticket stats:', error);
      return { success: false, error: error.message };
    }
  }

  async exportCustomerTicketsCSV(customerId, filters = {}) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const tickets = getTicketsByCustomerId(customerId);
          
          const headers = ['ID', 'Title', 'Status', 'Priority', 'Type', 'System', 'Site', 'Created', 'Due Date'];
          const rows = tickets.map(t => [
            t.id,
            `"${t.title}"`,
            t.status,
            t.priority,
            t.ticket_type,
            t.system_type,
            t.site_name || '',
            new Date(t.created_at).toLocaleDateString(),
            t.due_date ? new Date(t.due_date).toLocaleDateString() : ''
          ]);
          
          const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `my-tickets-export-${Date.now()}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
          
          resolve({ success: true });
        }, 500);
      });
    }

    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.site) params.site_id = filters.site;
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      const response = await api.get(`${API_ENDPOINTS.TICKETS_EXPORT_CSV}?customer_id=${customerId}`, {
        responseType: 'blob',params
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `my-tickets-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error exporting customer tickets:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new CustomerService();